import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Row, 
  Col, 
  Divider,
  message,
  Checkbox
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined,
  EyeInvisibleOutlined, 
  EyeTwoTone,
  BookOutlined 
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import authService from '../services/authService';

const { Title, Text } = Typography;

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  agree?: boolean;
}

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const handleRegister = async (values: RegisterFormData) => {
    setLoading(true);
    try {
      if (values.password !== values.confirmPassword) {
        message.error('Passwords do not match!');
        return;
      }

      const result = await authService.register({
        name: values.name,
        email: values.email,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      
      message.success('Registration successful!');
      navigate('/login');
    } catch (error: any) {
      console.error('Registration error:', error);
      message.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '8px' : '24px'
    }}>
      <Row style={{ width: '100%', maxWidth: '1200px' }}>
        <Col xs={24} lg={12} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: isMobile ? '0' : '0 24px'
        }}>
          {!isMobile && (
            <div style={{ textAlign: 'center', color: 'white' }}>
              <img 
                src="/images/logo.png" 
                alt="ICER Logo" 
                className="logo-image"
                style={{ 
                  width: '120px', 
                  height: '120px',
                  marginBottom: '24px',
                  objectFit: 'contain',
                  borderRadius: '12px'
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'block';
                  }
                }}
              />
              <BookOutlined 
                className="logo-fallback" 
                style={{ 
                  fontSize: '120px', 
                  marginBottom: '24px',
                  display: 'none',
                  color: 'white'
                }} 
              />
              <Title level={1} style={{ color: 'white', fontSize: '48px' }}>
                Join ICER LMS
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '18px' }}>
                Start your learning journey today
              </Text>
              <div style={{ marginTop: '32px' }}>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
                  🎓 Expert-led courses<br/>
                  📈 Track your progress<br/>
                  🏆 Earn certificates<br/>
                  🌟 Join thousands of learners
                </Text>
              </div>
            </div>
          )}
        </Col>
        
        <Col xs={24} lg={12} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Card 
            style={{ 
              width: '100%', 
              maxWidth: isMobile ? '350px' : '400px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              borderRadius: '12px'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '32px' }}>
              <img 
                src="/images/logo.png" 
                alt="ICER Logo" 
                className="logo-image"
                style={{ 
                  width: isMobile ? '150px' : '80px', 
                  height: isMobile ? '60px' : '80px',
                  marginBottom: '16px',
                  objectFit: 'contain',
                  borderRadius: '10px',
                  maxWidth: '100%'
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'block';
                  }
                }}
              />
              <BookOutlined 
                className="logo-fallback" 
                style={{ 
                  fontSize: isMobile ? '60px' : '80px', 
                  color: '#1890ff', 
                  marginBottom: '16px',
                  display: 'none'
                }} 
              />
              <Title level={2} style={{ marginBottom: '8px' }}>
                Create Account
              </Title>
              <Text type="secondary">
                Join our learning community
              </Text>
            </div>

            <Form
              form={form}
              name="register"
              onFinish={handleRegister}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="name"
                label="Full Name"
                rules={[
                  { required: true, message: 'Please input your full name!' },
                  { min: 2, message: 'Name must be at least 2 characters!' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter your full name"
                  autoComplete="name"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please input your password!' },
                  { min: 6, message: 'Password must be at least 6 characters!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter your password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm your password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="agree"
                valuePropName="checked"
                rules={[
                  { required: true, message: 'Please agree to the terms and conditions!' }
                ]}
              >
                <Checkbox>
                  I agree to the{' '}
                  <Link to="/terms" style={{ color: '#1890ff' }}>
                    Terms and Conditions
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" style={{ color: '#1890ff' }}>
                    Privacy Policy
                  </Link>
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  style={{ 
                    width: '100%',
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  Create Account
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Text type="secondary">
                Already have an account?{' '}
                <Link to="/login" style={{ color: '#1890ff', fontWeight: '600' }}>
                  Sign in here
                </Link>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Register;
